import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { flatten, noop, uniq } from 'lodash-es'
import { EntityType, EntityTypeEnum, OrganisationMeta, ReferenceDataManagementEvent, Severity, WorkflowActions } from 'api'
import { Locale, Translation } from 'locale'
import { currentOrganisationIdSelector } from 'selectors/adminSelectors'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { ValidationIssuesContainer, UnhappyError, withUnhappyModalBoundary } from 'view/shared/error'
import {
  OrgMetaCounterpartySelectboxField,
  OrgMetaProductMultiComboboxField,
  OrgMetaProductSectorMultiComboboxField
} from 'view/shared/entity'
import { OrgMetaCounterpartyOrganisationSelectboxField, OrgMetaTradingEntitySelectboxField } from 'view/shared/entity/'
import { UserSelectboxField } from 'view/shared/entity/UserSelectbox'
import { createInvalidValueValidationIssue } from 'model'
import { EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { notNilFilter } from 'utils'
import {
  useEntityGetter,
  useReferenceDataAvailableEvents,
  useReferenceDataEntities,
  useReferenceDataValidationIssues,
  useUpdateHandler
} from 'view/hooks'
import { ClonedComponentByShowModal } from 'view/shared/modal'

export interface ResponderSalesCoverageDialogProps extends ClonedComponentByShowModal {
  workflowId: string
}

const Content = styled(Groupbox)`
  height: 400px;
  width: 510px;
  padding: 20px 20px 0 20px;
  overflow: auto;
`

const ResponderSalesCoverageDialogComponent = ({ workflowId, onClose }: ResponderSalesCoverageDialogProps) => {
  const organisationMetaEntities = useReferenceDataEntities(EntityType.ORGANISATION_META, workflowId)

  if (organisationMetaEntities.length === 0) {
    throw new UnhappyError('Missing organisation meta entities.')
  }

  const dispatch = useDispatch()
  const getOrganisation = useEntityGetter(EntityType.ORGANISATION)
  const availableEvents = useReferenceDataAvailableEvents(workflowId)
  const workflowValidationIssues = useReferenceDataValidationIssues(workflowId)
  const currentOrganisationId = useSelector(currentOrganisationIdSelector)

  const organisationMetas = useMemo(() => organisationMetaEntities.map(organisationMetaEntity => organisationMetaEntity.data), [
    organisationMetaEntities
  ])

  const firstOrganisationMeta = useMemo(() => organisationMetas[0], [organisationMetas])
  const remainingOrganisationMetas = organisationMetas.slice(1)
  const organisation = getOrganisation(firstOrganisationMeta.ownerOrganisationId)
  const productIds = uniq(organisationMetas.map(organisationMeta => organisationMeta.productId).filter(notNilFilter))
  const productSectorIds = organisationMetas.map(organisationMeta => organisationMeta.productSectorId).filter(notNilFilter)
  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const serverValidationIssues = flatten(organisationMetaEntities.map(entity => entity.validationIssues ?? []))
  const localValidationIssues = [
    ...(serverValidationIssues.filter(serverValidationIssue => serverValidationIssue.fieldName === 'organisationMeta').length > 0
      ? [createInvalidValueValidationIssue('organisationMeta', Locale.translate(Translation.SC_DUPLICATE_SALES_COVER))]
      : []),
    ...(firstOrganisationMeta.primaryCoverUserId === null && firstOrganisationMeta.secondaryCoverUserId === null
      ? [createInvalidValueValidationIssue('organisationMeta', Locale.translate(Translation.SC_BOTH_COVER_USERS_NOT_SET), Severity.ERROR)]
      : [])
  ]
  const validationIssues = serverValidationIssues
    .filter(serverValidationIssue => serverValidationIssue.fieldName !== 'organisationMeta')
    .concat(localValidationIssues)
  const hasValidationIssues = validationIssues.length > 0 || workflowValidationIssues.length > 0

  const handleUpdateFirstOrganisationMeta = (partialOrganisationMeta: Partial<OrganisationMeta>) => {
    dispatch(
      WorkflowActions.createEntityUpdateReferenceData<OrganisationMeta>({
        entityId: firstOrganisationMeta.id,
        entityType: EntityTypeEnum.ORGANISATION_META,
        updatedEntity: { ...firstOrganisationMeta, ...partialOrganisationMeta },
        workflowId
      })
    )
  }

  const handleUpdateOrganisationMetas = useCallback(
    (partialOrganisationMeta: Partial<OrganisationMeta>) => {
      organisationMetas.forEach(organisationMeta => {
        dispatch(
          WorkflowActions.createEntityUpdateReferenceData<OrganisationMeta>({
            entityId: organisationMeta.id,
            entityType: EntityTypeEnum.ORGANISATION_META,
            updatedEntity: {
              ...organisationMeta,
              ...partialOrganisationMeta
            },
            workflowId
          })
        )
      })
    },
    [dispatch, organisationMetas, workflowId]
  )

  const updateHandler = useUpdateHandler(handleUpdateOrganisationMetas)

  const handleUpdateTradingEntityId = (value: string) => {
    handleDeleteRemaining()
    handleUpdateFirstOrganisationMeta({
      ownerTradingEntityId: value,
      subjectOrganisationId: null,
      subjectTradingEntityId: null,
      productId: null,
      productSectorId: null
    })
  }

  const handleUpdateCounterpartyOrganisationId = (value: string) => {
    handleDeleteRemaining()
    handleUpdateFirstOrganisationMeta({
      subjectOrganisationId: value,
      subjectTradingEntityId: null,
      productId: null,
      productSectorId: null
    })
  }

  const handleUpdateCounterpartyId = (value: string) => {
    handleDeleteRemaining()
    handleUpdateFirstOrganisationMeta({
      subjectTradingEntityId: value,
      productId: null,
      productSectorId: null
    })
  }

  const handleAddNewProductIds = (newProductIds: string[]) => {
    newProductIds.forEach(productId =>
      dispatch(
        WorkflowActions.createAddToExistingWorkflow(
          workflowId,
          EntityTypeEnum.ORGANISATION_META,
          (_workflowId: String, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
            dispatch(
              WorkflowActions.createEntityUpdateReferenceData<OrganisationMeta>({
                entityId: newlyAddedEntityIdAndTypes[EntityTypeEnum.ORGANISATION_META],
                entityType: EntityTypeEnum.ORGANISATION_META,
                updatedEntity: { ...firstOrganisationMeta, id: newlyAddedEntityIdAndTypes[EntityTypeEnum.ORGANISATION_META], productId },
                workflowId
              })
            )
          }
        )
      )
    )
  }

  const handleUpdateProductIds = (values: string[]) => {
    if (values.length === 0) {
      // delete all from workflow but one and set to null
      handleDeleteRemaining()
      handleUpdateFirstOrganisationMeta({
        productId: null,
        productSectorId: null
      })
      return
    }

    const removedProductIds = productIds.filter(productId => !values.includes(productId))
    // delete from workflow
    if (removedProductIds.length > 0) {
      dispatch(
        WorkflowActions.createEntityDeleteReferenceData(
          organisationMetas
            .filter(organisationMeta => organisationMeta.productId && removedProductIds.includes(organisationMeta.productId))
            .map(organisationMeta => ({ entityId: organisationMeta.id, entityType: EntityTypeEnum.ORGANISATION_META })),
          workflowId
        )
      )
    }

    const newProductIds = values.filter(value => !productIds.includes(value))

    if (newProductIds.length === 0) {
      return
    } else if (productIds.length === 0) {
      handleUpdateFirstOrganisationMeta({
        productId: newProductIds[0],
        productSectorId: null
      })
      // add to existing workflow
      handleAddNewProductIds(newProductIds.slice(1))
    } else {
      handleAddNewProductIds(newProductIds)
    }
  }

  const handleAddNewProductSectorIds = (newProductSectorIds: string[]) => {
    newProductSectorIds.forEach(productSectorId =>
      dispatch(
        WorkflowActions.createAddToExistingWorkflow(
          workflowId,
          EntityTypeEnum.ORGANISATION_META,
          (_workflowId: String, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
            dispatch(
              WorkflowActions.createEntityUpdateReferenceData<OrganisationMeta>({
                entityId: newlyAddedEntityIdAndTypes[EntityTypeEnum.ORGANISATION_META],
                entityType: EntityTypeEnum.ORGANISATION_META,
                workflowId,
                updatedEntity: {
                  ...firstOrganisationMeta,
                  id: newlyAddedEntityIdAndTypes[EntityTypeEnum.ORGANISATION_META],
                  productSectorId
                }
              })
            )
          }
        )
      )
    )
  }

  const handleUpdateProductSectorIds = (values: string[]) => {
    if (values.length === 0) {
      // delete all from workflow but one and set to null
      handleDeleteRemaining()
      handleUpdateFirstOrganisationMeta({ productSectorId: null })
      return
    }

    const removedProductSectorIds = productSectorIds.filter(productSectorId => !values.includes(productSectorId))
    // delete from workflow
    if (removedProductSectorIds.length > 0) {
      dispatch(
        WorkflowActions.createEntityDeleteReferenceData(
          organisationMetas
            .filter(
              organisationMeta => organisationMeta.productSectorId && removedProductSectorIds.includes(organisationMeta.productSectorId)
            )
            .map(organisationMeta => ({ entityId: organisationMeta.id, entityType: EntityTypeEnum.ORGANISATION_META })),
          workflowId
        )
      )
    }

    const newProductSectorIds = values.filter(value => !productSectorIds.includes(value))
    if (newProductSectorIds.length === 0) {
      return
    } else if (productSectorIds.length === 0) {
      handleUpdateFirstOrganisationMeta({
        productSectorId: newProductSectorIds[0]
      })
      // add to existing workflow
      handleAddNewProductSectorIds(newProductSectorIds.slice(1))
    } else {
      handleAddNewProductSectorIds(newProductSectorIds)
    }
  }

  const handleDeleteRemaining = () => {
    if (remainingOrganisationMetas.length > 0) {
      dispatch(
        WorkflowActions.createEntityDeleteReferenceData(
          remainingOrganisationMetas.map(organisationMeta => ({
            entityId: organisationMeta.id,
            entityType: EntityTypeEnum.ORGANISATION_META
          })),
          workflowId
        )
      )
    }
  }

  const handleSave = () => {
    dispatch(WorkflowActions.createSubmitWorkflow(workflowId))
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    dispatch(WorkflowActions.createCancelWorkflow(workflowId))
    if (onClose) {
      onClose()
    }
  }

  useEffect(() => {
    if (firstOrganisationMeta.ownerOrganisationId === null && currentOrganisationId) {
      updateHandler('ownerOrganisationId')(currentOrganisationId)
    }
  }, [currentOrganisationId, firstOrganisationMeta, updateHandler])

  return (
    <ModalWindow title={Locale.translate(Translation.TC_NEW_SALES_COVERAGE)}>
      <Content>
        <ValidationIssuesContainer>
          {validationIssues
            ?.filter(issue => issue.fieldName === 'organisationMeta')
            .map((issue, index) => (
              <li key={index}>{issue.errorMessage}</li>
            ))}
        </ValidationIssuesContainer>
        <DialogRow>
          <DialogField>
            <TextInputField
              label={Locale.translate(Translation.SC_ORGANISATION_META_MY_FIRM)}
              value={organisation?.legalName}
              onUpdate={noop}
              readOnly={true}
            />
          </DialogField>
          <DialogField>
            <OrgMetaTradingEntitySelectboxField
              fieldName="ownerTradingEntityId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_MY_ENTITY)}
              organisationId={firstOrganisationMeta.ownerOrganisationId}
              selectedTradingEntityId={firstOrganisationMeta.ownerTradingEntityId}
              onUpdate={handleUpdateTradingEntityId}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <OrgMetaCounterpartyOrganisationSelectboxField
              fieldName="subjectOrganisationId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_COUNTERPARTY_FIRM)}
              organisationId={firstOrganisationMeta.ownerOrganisationId}
              selectedTradingEntityId={firstOrganisationMeta.ownerTradingEntityId}
              selectedCounterpartyOrganisationId={firstOrganisationMeta.subjectOrganisationId}
              onUpdate={handleUpdateCounterpartyOrganisationId}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <OrgMetaCounterpartySelectboxField
              fieldName="subjectTradingEntityId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_COUNTERPARTY)}
              selectedTradingEntityId={firstOrganisationMeta.ownerTradingEntityId}
              selectedCounterpartyOrganisationId={firstOrganisationMeta.subjectOrganisationId}
              selectedCounterpartyId={firstOrganisationMeta.subjectTradingEntityId}
              onUpdate={handleUpdateCounterpartyId}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <OrgMetaProductMultiComboboxField
              fieldName="productId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_PRODUCT)}
              organisationId={firstOrganisationMeta.ownerOrganisationId}
              selectedTradingEntityId={firstOrganisationMeta.ownerTradingEntityId}
              selectedProductIds={productIds}
              selectedProductSectorIds={productSectorIds}
              onUpdate={handleUpdateProductIds}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
          <DialogField>
            <OrgMetaProductSectorMultiComboboxField
              fieldName="productSectorId"
              label={Locale.translate(Translation.SC_ORGANISATION_META_PRODUCT_SECTOR)}
              organisationId={firstOrganisationMeta.ownerOrganisationId}
              selectedTradingEntityId={firstOrganisationMeta.ownerTradingEntityId}
              selectedProductIds={productIds}
              selectedProductSectorIds={productSectorIds}
              onUpdate={handleUpdateProductSectorIds}
              readOnly={readOnly}
              validationIssues={validationIssues}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <UserSelectboxField
              label={Locale.translate(Translation.TC_PRIMARY_COVER)}
              value={firstOrganisationMeta.primaryCoverUserId}
              onUpdate={updateHandler('primaryCoverUserId')}
            />
          </DialogField>
          <DialogField>
            <UserSelectboxField
              label={Locale.translate(Translation.TC_SECONDARY_COVER)}
              value={firstOrganisationMeta.secondaryCoverUserId}
              onUpdate={updateHandler('secondaryCoverUserId')}
            />
          </DialogField>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton type={TextButtonType.Primary} disabled={readOnly || hasValidationIssues} onClick={handleSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ResponderSalesCoverageDialog = withUnhappyModalBoundary(ResponderSalesCoverageDialogComponent)
